import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useAuth } from "@/components/auth/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function SharedProjectsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: sharedProjects, isError } = useQuery({
    queryKey: ["shared-projects"],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("project_shares")
        .select(`
          *,
          projects:project_id (
            name,
            description,
            user_id
          ),
          shared_by_profile:profiles!project_shares_shared_by_fkey (
            full_name,
            email
          )
        `)
        .eq("shared_with", user.id);

      if (error) {
        console.error("Error fetching shared projects:", error);
        throw error;
      }

      // Filter out projects where the current user is the owner
      return data?.filter(share => share.projects?.user_id !== user.id) || [];
    },
    enabled: !!user,
  });

  const handleLeaveProject = async (shareId: string) => {
    try {
      const { error } = await supabase
        .from("project_shares")
        .delete()
        .eq("id", shareId);

      if (error) throw error;

      queryClient.setQueryData(["shared-projects"], (oldData: any) => {
        return oldData?.filter((share: any) => share.id !== shareId) ?? [];
      });

      toast.success("Successfully left the project");
    } catch (error) {
      console.error("Error leaving project:", error);
      toast.error("Failed to leave project");
    }
  };

  if (isError) {
    return (
      <div className="text-center p-4">
        <p className="text-sm text-muted-foreground">Failed to load shared projects</p>
      </div>
    );
  }

  if (!sharedProjects?.length) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold">Shared with you</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sharedProjects.map((share) => (
          <Card 
            key={share.id} 
            className="hover:shadow-lg transition-shadow"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle 
                className="text-base font-semibold cursor-pointer"
                onClick={() => navigate(`/project/${share.project_id}`)}
              >
                {share.projects?.name || 'Unnamed Project'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant={share.permission === 'edit' ? 'default' : 'secondary'}>
                  {share.permission === 'edit' ? 'Can edit' : 'View only'}
                </Badge>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Leave Project</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to leave this project? You'll need to be invited again to regain access.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleLeaveProject(share.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Leave Project
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                {share.projects?.description || 'No description'}
              </p>
              <p className="text-xs text-muted-foreground">
                Shared by {share.shared_by_profile?.full_name || share.shared_by_profile?.email || 'Unknown'} on{' '}
                {format(new Date(share.created_at), 'MMM d, yyyy')}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}